import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import {
  Plus,
  Wallet,
  Search,
  RefreshCcw,
  X,
  ChevronUp,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Lock,
  Unlock,
  CreditCard,
  ArrowRightLeft,
  Calendar,
  User,
  Activity,
  Printer,
  AlertCircle,
  Trash2,
  Check,
  Edit2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { processPendingBankSettlements } from '../utils/bankSettlements';
import { parseLocalizedNumber } from '../utils/numberFormat';
import { useSite } from '../context/SiteContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useCashCloseAccess } from '../hooks/useCashCloseAccess';
import { canReviewArqueos } from '../utils/arqueoReview';
import ControlArqueosPanel from './caja/ControlArqueosPanel';
import {
  getLocalDateKey,
} from '../utils/localDateTime';
import { desactivarAvisosAdminEsteDispositivo } from '../utils/pushSubscription';

// El estado "anulado" de un movimiento de caja vive como prefijo en la
// descripcion. Al editarla se saca para que nadie anule (o "desanule") un
// movimiento escribiendo el texto: eso dejaria los saldos sin revertir.
const ANULADO_TAG_RE = /\(ANULADO\)/gi;

function EditarDescripcionMovimiento({ mov, onSave }) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => { setTexto(mov.descripcion || ''); setAbierto(true); }}
        className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] hover:border-[#51A7FD]/40 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm"
      >
        <Edit2 size={14} /> Editar descripción
      </button>
    );
  }

  const guardar = async () => {
    setGuardando(true);
    const ok = await onSave(mov, texto);
    setGuardando(false);
    if (ok) setAbierto(false);
  };

  return (
    <div className="flex flex-1 items-center gap-2">
      <input
        autoFocus
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') setAbierto(false); }}
        maxLength={200}
        disabled={guardando}
        className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase text-slate-700 outline-none focus:border-[#51A7FD]"
      />
      <button type="button" onClick={guardar} disabled={guardando} title="Guardar" className="rounded-lg bg-[#0B2A36] p-2 text-white disabled:opacity-50">
        {guardando ? <RefreshCcw size={12} className="animate-spin" /> : <Check size={12} />}
      </button>
      <button type="button" onClick={() => setAbierto(false)} disabled={guardando} title="Cancelar" className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[9px] font-black uppercase text-slate-500">
        No
      </button>
    </div>
  );
}

const DEFAULT_ARQUEO_PRINT_CONFIG = {
  headerText: 'Arqueo de Caja',
  subHeaderText: 'Control Interno',
  footerText: 'Firma Cajero: .................',
  showLogo: false,
  logoBase64: null,
  printerWidth: '80mm',
  showPaymentMethod: true,
  printAction: 'ask',
};

/**
 * Caja.jsx - Revisión Integral & Fix de Cierre
 * Solución al problema de cierre: Match robusto de métodos de pago (ID o Nombre).
 * Estilo UI: Estándar Productos (Header -> KPIs -> Filtros -> Lista).
 * NUEVO: Integración FULL de Gastos/Pagos a Proveedores.
 * NUEVO: Anulación inline ("Sí, borrar" / "No") con rollback completo.
 * FIX: Manejo de restricción de base de datos (monto > 0).
 */
function Caja() {
  const navigate = useNavigate();
  const { profile, organization, signOut } = useAuth();
  // ✅ FIX multi-sucursal (reactivo): consumimos el sitio del SiteContext en lugar
  // de leer localStorage. Esto permite que Caja se entere del cambio de sucursal
  // que hace el Topbar SIN reload y vuelva a cargar turno/movimientos/ventas
  // de la sucursal nueva.
  const { selectedSiteId } = useSite();
  // ✅ NUEVO offline: bloqueamos apertura/cierre/movimientos sin red.
  const isOnline = useOnlineStatus();
  // Cierre ciego: los permisos finos de caja (`caja-cierre-completo`,
  // `caja-ver-movimientos`, `caja-ver-ultimos-movimientos`,
  // `caja-imprimir-cierre`, `caja-anular-movimiento`) solo restringen cuando la
  // organizacion tiene el cierre ciego activo. Ver utils/cashCloseVisibility.js.
  const {
    canViewFullClose,
    canViewMovements,
    canViewRecentMovements,
    canPrintClose,
    canVoidMovements,
    recentMovementsLimit,
    resolvingCashCloseAccess,
    isSuperAdmin,
  } = useCashCloseAccess();

  const [loading, setLoading] = useState(true);

  // Estado core
  const [turnoActual, setTurnoActual] = useState(null);
  const [historialCierres, setHistorialCierres] = useState([]);
  // Cierre recién realizado en esta sesión: es el comprobante que el usuario sin
  // acceso al historial todavía tiene derecho a imprimir.
  const [ultimoCierrePropio, setUltimoCierrePropio] = useState(null);
  
  // Datos para cálculo
  const [movimientosTurno, setMovimientosTurno] = useState([]);
  const [ventasTurno, setVentasTurno] = useState([]); 
  const [metodosCobro, setMetodosCobro] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);
  const [arqueoPrintConfig, setArqueoPrintConfig] = useState(DEFAULT_ARQUEO_PRINT_CONFIG);

  // UI states
  // Al volver del detalle de un arqueo se reabre la pestaña de origen (control
  // o historial). `activeTab` igual la baja a 'turno' si no hay permiso.
  const location = useLocation();
  const tabRetornoRef = useRef(
    location.state?.tab === 'control' || location.state?.tab === 'historial' ? location.state.tab : null,
  );
  const [tab, setTab] = useState(() => tabRetornoRef.current || 'turno'); // 'turno' | 'historial' | 'control'
  // La carga del turno elige pestaña (turno abierto => 'turno'); la primera vez
  // después de volver del detalle manda la pestaña de origen.
  const setTabInicial = (porDefecto) => {
    const retorno = tabRetornoRef.current;
    tabRetornoRef.current = null;
    setTab(retorno || porDefecto);
  };
  // Arqueos cerrados sin controlar de toda la organizacion (lo cuenta el panel
  // de control con head:true; se muestra en el tab).
  const [pendientesControl, setPendientesControl] = useState(0);
  const [q, setQ] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [processingDeleteId, setProcessingDeleteId] = useState(null);
  const [movimientoSortConfig, setMovimientoSortConfig] = useState({ key: 'fecha', direction: 'desc' });
  const [cierreSortConfig, setCierreSortConfig] = useState({ key: 'fecha_cierre', direction: 'desc' });

  // Modales
  const [showApertura, setShowApertura] = useState(false);
  const [showCierre, setShowCierre] = useState(false);
  const [showMovimiento, setShowMovimiento] = useState(false);

  // Formularios
  const [formApertura, setFormApertura] = useState({ saldo_inicial: 0, observaciones: '' });
  
  const [formMov, setFormMov] = useState({
    tipo: 'Gasto',
    categoria: 'Variable', 
    monto: '',
    descripcion: '',
    payment_method_id: '',
    proveedor_id: ''
  });

  // Arqueo
  const [arqueoReal, setArqueoReal] = useState({});

  // --- REFERENCIA HORARIA DE CAJA (CRÍTICO PARA EL CIERRE) ---
  //
  // El resto de la app —y en particular VentasPOS, que escribe `sales.created_at`—
  // guarda la hora local del comercio etiquetada como UTC ("forjada"). El cierre
  // compara `sales.created_at >= turnos_caja.fecha_apertura`, así que la apertura
  // TIENE que escribirse con el mismo reloj forjado.
  //
  // Escribirla como instante real (con offset) mientras el POS sigue forjando dejó
  // la apertura 3 h adelante de las ventas y el arqueo perdió todo lo vendido en
  // las primeras 3 h del turno (bug del 11/08/2026 en Don Saro y Kilimanjaro).
  //
  // Migrar a instantes reales es lo correcto, pero es un cambio atómico: hay que
  // mover POS, gastos, compras y tesorería a la vez y hacer backfill de lo viejo.
  // Ver docs/specs/p1-timestamps-locales.md. Hasta entonces, acá se forja.
  const getSystemOperationTimestamp = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');

    return `${getLocalDateKey(now)}T${hh}:${mm}:${ss}.000Z`;
  };

  // Los timestamps guardados son hora local con una "Z" pegada: se leen en UTC,
  // que es justo lo que los devuelve a la hora del comercio. Convertirlos a hora
  // local los correría 3 h hacia atrás.
  const formatDateAR = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('es-AR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatDateTimeAR = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('es-AR', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Mismo criterio para el historial de cierres.
  const formatFechaCierre = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('es-AR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatHoraTurno = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('es-AR', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const renderMultiline = (value) => escapeHtml(value).replaceAll('\n', '<br />');

  useEffect(() => {
    // ✅ FIX multi-sucursal (reactivo): re-inicializamos Caja también cuando
    // cambia la sucursal seleccionada. Antes el efecto sólo escuchaba
    // organization_id, así que cambiar de sucursal en el Topbar dejaba la
    // pantalla mostrando el turno y las ventas de la sucursal anterior.
    if (profile?.organization_id) initCaja();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, selectedSiteId]);

  // La organizacion del AuthContext se resuelve async y puede llegar despues del
  // profile (o en un segundo fetch, si el embed organizations(*) no esta
  // disponible). Sin este efecto, organizationData se quedaba con el valor del
  // primer render — null — hasta que cambiara la sucursal o la organizacion:
  // el cierre ciego no ocultaba los importes de control y la pestaña de control
  // de arqueos no aparecia. applyOrganizationSettings solo copia estado, no
  // hace fetch, asi que repetirlo es gratis.
  useEffect(() => {
    if (organization) applyOrganizationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // El permiso se resuelve async, así que las funciones que ya están en vuelo lo
  // leen por ref y no por closure (evita decidir con un valor viejo).
  const canViewFullCloseRef = useRef(canViewFullClose);
  useEffect(() => {
    canViewFullCloseRef.current = canViewFullClose;
  }, [canViewFullClose]);

  // Mismo motivo para las dos acciones: el handler puede haberse creado antes de
  // que el permiso terminara de resolverse.
  const canPrintCloseRef = useRef(canPrintClose);
  useEffect(() => {
    canPrintCloseRef.current = canPrintClose;
  }, [canPrintClose]);

  const canVoidMovementsRef = useRef(canVoidMovements);
  useEffect(() => {
    canVoidMovementsRef.current = canVoidMovements;
  }, [canVoidMovements]);

  const atajoHistorialAplicadoRef = useRef(false);
  useEffect(() => {
    // Cambiar de sucursal reinicia la pantalla: el atajo vuelve a estar en juego.
    atajoHistorialAplicadoRef.current = false;
  }, [profile?.organization_id, selectedSiteId]);

  // El historial se trae SOLO cuando el permiso ya está resuelto y concedido: si
  // el usuario no puede verlo, sus importes no deben ni llegar al navegador.
  useEffect(() => {
    if (!profile?.organization_id) return;
    if (resolvingCashCloseAccess) return;

    if (!canViewFullClose) {
      setHistorialCierres([]);
      return;
    }

    fetchHistorialCierres()
      .then(() => {
        // `checkTurnoActivo` corre antes de que el permiso esté resuelto, así que
        // un usuario CON permiso perdía el atajo histórico de caer en el
        // historial cuando no hay turno abierto. Lo recuperamos una sola vez,
        // sin pisar una elección posterior del usuario.
        if (atajoHistorialAplicadoRef.current) return;
        atajoHistorialAplicadoRef.current = true;
        setTurnoActual((current) => {
          if (!current) setTab('historial');
          return current;
        });
      })
      .catch((err) => console.error('Error al cargar historial de cierres:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, selectedSiteId, canViewFullClose, resolvingCashCloseAccess]);

  const handleAuthError = async (error) => {
    console.error("Error crítico de caja:", error);
    if (error?.message?.includes('Refresh Token') || error?.code === '401') {
        toast.error("Sesión expirada.");
        if (signOut) await signOut();
        else {
          // Avisos push: este equipo deja de recibir los pedidos de la empresa.
          await desactivarAvisosAdminEsteDispositivo();
          await supabase.auth.signOut();
        }
        navigate('/login');
    } else {
        toast.error('Error: ' + (error?.message || 'Revisa tu conexión'));
    }
  };

  async function initCaja() {
    setLoading(true);
    try {
      await processPendingBankSettlements(supabase, profile.organization_id);
      applyOrganizationSettings();
      await fetchMetodos();
      await fetchProveedores();
      await checkTurnoActivo();
      // El historial lo dispara su propio efecto, recién cuando el permiso de
      // cierre completo está resuelto.
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }

  function applyOrganizationSettings() {
    // organization viene del AuthContext (un fetch por sesión) — evita el N+1
    // que detectaba Sentry cuando cada página repetía `from('organizations')`.
    setOrganizationData(organization || null);
    setArqueoPrintConfig({
      ...DEFAULT_ARQUEO_PRINT_CONFIG,
      ...(organization?.print_config?.arqueo || {}),
    });
  }

  const isBlindCashCloseEnabled = Boolean(organizationData?.blind_cash_close);
  // Saldo inicial y egresos tambien son insumos del teorico: con cierre ciego,
  // quien no tiene `caja-cierre-completo` no los ve. Para quien SI lo tiene se
  // mantiene lo de antes (el flag ya tapaba ingresos y teorico para todos).
  const ocultarImportesDeControl = isBlindCashCloseEnabled && !canViewFullClose;

  // Control de arqueos: pestaña aparte, con `organizations.enable_arqueo_review`
  // activo y solo para admin/dueño. Es independiente del cierre ciego y de
  // `caja-cierre-completo`: ver el arqueo entero no es lo mismo que aprobarlo.
  // La misma regla la refuerza el trigger tr_turnos_caja_guard_control.
  // Lee `organization` del context, no la copia en estado: una regla de acceso
  // no puede depender de que un efecto ya haya corrido.
  const puedeControlarArqueos = canReviewArqueos({
    organization,
    profile,
    isSuperAdmin,
  });

  async function fetchMetodos() {
    const { data, error } = await supabase
      .from('payment_methods')
      .select('*, bank_id') 
      .eq('organization_id', profile.organization_id)
      .neq('is_active', false);
    if (error) throw error;
    setMetodosCobro(data || []);
  }

  async function fetchProveedores() {
    const { data, error } = await supabase
      .from('proveedores')
      .select('id, razon_social, saldo')
      .eq('organization_id', profile.organization_id)
      .eq('is_active', true)
      .order('razon_social', { ascending: true });
    if (error) throw error;
    setProveedores(data || []);
  }

  async function checkTurnoActivo() {
    // ✅ FIX multi-sucursal (reactivo): usamos selectedSiteId del SiteContext
    // como fuente de verdad — así el cambio de sucursal en el Topbar se
    // refleja inmediatamente acá (el efecto que llama a initCaja ya depende
    // de selectedSiteId).
    const sitioId = selectedSiteId || '';

    let query = supabase
      .from('turnos_caja')
      .select('*')
      .eq('organization_id', profile.organization_id)
      .eq('estado', 'Abierto');

    if (sitioId) {
      query = query.eq('sitio_id', sitioId);
    }

    // Nunca .maybeSingle() a secas: con dos turnos abiertos (sin sucursal
    // elegida, o un doble "Abrir caja") PostgREST devuelve "JSON object
    // requested, multiple (or no) rows returned" y la Caja queda bloqueada
    // (caso Memo 26/10/2026). Tomamos el más reciente y avisamos.
    const { data: abiertos, error } = await query
      .order('fecha_apertura', { ascending: false })
      .limit(2);

    if (error) throw error;

    const data = abiertos?.[0] || null;
    if ((abiertos?.length || 0) > 1) {
      toast(
        sitioId
          ? 'Hay más de un turno abierto en esta sucursal. Se muestra el más reciente: cerrá el otro desde el historial.'
          : 'Hay turnos abiertos en más de una sucursal. Elegí la sucursal arriba para ver el suyo.',
        { id: 'caja-turnos-multiples', icon: '⚠️', duration: 8000 }
      );
    }

    if (data) {
      setTurnoActual(data);
      await Promise.all([
          fetchMovimientosTurno(data.id),
          fetchVentasTurno(data.fecha_apertura, data.sitio_id || sitioId)
      ]);
      setTabInicial('turno');
    } else {
      setTurnoActual(null);
      setMovimientosTurno([]);
      setVentasTurno([]);
      // Sin turno abierto el atajo era saltar al historial. Con cierre ciego y
      // sin permiso ese atajo filtraba justo lo que hay que ocultar.
      setTabInicial(canViewFullCloseRef.current ? 'historial' : 'turno');
    }
  }

  async function fetchMovimientosTurno(turnoId) {
    const { data, error } = await supabase
      .from('movimientos_caja')
      .select('*, payment_methods(name)')
      .eq('turno_id', turnoId)
      .order('fecha', { ascending: false });

    if (error) throw error;
    setMovimientosTurno(data || []);
  }

  async function fetchVentasTurno(fechaInicio, sitioId) {
      if (!fechaInicio) return;
      // ✅ FIX multi-sucursal (reactivo): aceptamos sitio_id como segundo parámetro y,
      // si no llega, usamos el selectedSiteId del SiteContext (mismo que ya disparó
      // este efecto).
      const sitioFiltro = sitioId || selectedSiteId || '';

      let query = supabase
        .from('sales')
        .select('id, total, payment_method_id, created_at, payment_method, site_id')
        .eq('organization_id', profile.organization_id)
        .gte('created_at', fechaInicio)
        .neq('status', 'canceled')
        .neq('status', 'anulada')
        .neq('invoice_status', 'presupuesto')
        .neq('active', false)
        // Ventas administrativas (a cuenta corriente, cargadas desde
        // Facturacion): no entraron al mostrador, no van al teorico.
        .not('afecta_caja', 'is', false);

      if (sitioFiltro) {
        query = query.eq('site_id', sitioFiltro);
      }

      const { data, error } = await query;

      if (error) {
          console.error("Error fetching ventas:", error);
          return;
      }
      setVentasTurno(data || []);
  }

  async function fetchHistorialCierres() {
    // Guarda de defensa: cualquier llamador (refresh manual, post-cierre) queda
    // cubierto sin repetir el chequeo en cada punto de llamada.
    if (!canViewFullCloseRef.current) {
      setHistorialCierres([]);
      return;
    }

    const { data, error } = await supabase
      .from('turnos_caja')
      .select('*')
      .eq('organization_id', profile.organization_id)
      .eq('estado', 'Cerrado')
      .order('fecha_cierre', { ascending: false })
      .limit(50);

    if (error) throw error;
    setHistorialCierres(data || []);
  }

  const goToArqueoDetalle = (turnoId, origen = 'historial') => {
    if (!turnoId) return;
    // CajaDetalle tiene su propio guard, pero cortamos acá para no navegar a una
    // pantalla que va a rebotar.
    if (!canViewFullCloseRef.current) {
      toast.error('No tenés permiso para ver el detalle del cierre.');
      return;
    }
    navigate(`/sistema/caja/detalle/${turnoId}`, { state: { from: origen } });
  };

  const buildCierreCajaHtml = (cierre) => {
    const config = arqueoPrintConfig || DEFAULT_ARQUEO_PRINT_CONFIG;
    const width = config.printerWidth === '58mm' ? '58mm' : config.printerWidth === 'a4' ? '210mm' : '80mm';
    const isThermal = width !== '210mm';
    const resumen = Array.isArray(cierre?.resumen_cierre) ? cierre.resumen_cierre : [];
    const header = config.headerText || organizationData?.name || 'Cierre de Caja';
    const subHeader =
      config.subHeaderText ||
      [organizationData?.full_address, organizationData?.tax_condition].filter(Boolean).join('\n');
    const footer = config.footerText || '';
    const companyName = organizationData?.name || '';
    const companyCuit = organizationData?.cuit || '';
    const openingNotes = cierre?.observaciones_apertura || '';

    // Cierre ciego sin permiso: el ticket sale con lo declarado y nada mas. Si
    // imprimiera teorico y diferencia, el arqueo a ciegas se caeria por papel.
    const showTheoretical = canViewFullCloseRef.current;
    const resumenColSpan = showTheoretical ? 4 : 2;

    const resumenRows = resumen.length > 0
      ? resumen
          .map((item) => {
            const diferencia = Number(item?.diferencia || 0);
            if (!showTheoretical) {
              return `
              <tr>
                <td>${escapeHtml(item?.nombre || 'Metodo')}</td>
                <td class="right">$ ${money(item?.real)}</td>
              </tr>
            `;
            }
            return `
              <tr>
                <td>${escapeHtml(item?.nombre || 'Metodo')}</td>
                <td class="right">$ ${money(item?.teorico)}</td>
                <td class="right">$ ${money(item?.real)}</td>
                <td class="right ${diferencia < 0 ? 'negative' : 'positive'}">$ ${money(diferencia)}</td>
              </tr>
            `;
          })
          .join('')
      : `
        <tr>
          <td colspan="${resumenColSpan}" class="empty">Sin detalle por metodo de cobro.</td>
        </tr>
      `;

    return `
<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Cierre de caja</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: ${isThermal ? '12px' : '24px'};
      font-family: Arial, sans-serif;
      color: #0f172a;
      background: #ffffff;
    }
    .sheet {
      width: ${width};
      max-width: 100%;
      margin: 0 auto;
    }
    .center { text-align: center; }
    .muted { color: #64748b; }
    .title {
      font-size: ${isThermal ? '16px' : '24px'};
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .subtitle {
      margin-top: 6px;
      font-size: ${isThermal ? '10px' : '12px'};
      line-height: 1.5;
    }
    .logo {
      max-width: ${isThermal ? '120px' : '180px'};
      max-height: ${isThermal ? '70px' : '90px'};
      margin: 0 auto 12px;
      display: block;
    }
    .separator {
      border-top: 1px dashed #94a3b8;
      margin: 12px 0;
    }
    .block {
      margin-bottom: 12px;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: ${isThermal ? '10px' : '12px'};
    }
    .block-title {
      margin-bottom: 8px;
      font-size: ${isThermal ? '10px' : '11px'};
      font-weight: 800;
      color: #334155;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 6px;
      font-size: ${isThermal ? '10px' : '12px'};
    }
    .row:last-child { margin-bottom: 0; }
    .label { color: #64748b; }
    .value { font-weight: 700; text-align: right; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: ${isThermal ? '9px' : '11px'};
    }
    th, td {
      padding: 7px 6px;
      border-bottom: 1px solid #e2e8f0;
    }
    th {
      text-align: left;
      color: #475569;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      font-size: ${isThermal ? '8px' : '10px'};
    }
    .right { text-align: right; }
    .totals {
      margin-top: 12px;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      padding: ${isThermal ? '10px' : '14px'};
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 8px;
      font-size: ${isThermal ? '10px' : '13px'};
      font-weight: 700;
    }
    .total-row:last-child { margin-bottom: 0; }
    .grand-total {
      font-size: ${isThermal ? '12px' : '16px'};
      font-weight: 800;
      text-transform: uppercase;
    }
    .positive { color: #059669; }
    .negative { color: #e11d48; }
    .empty {
      text-align: center;
      color: #94a3b8;
      font-style: italic;
      padding: 14px 8px;
    }
    .footer {
      margin-top: 16px;
      text-align: center;
      color: #64748b;
      font-size: ${isThermal ? '9px' : '11px'};
      line-height: 1.5;
    }
    @media print {
      body { padding: 0; }
      .sheet { width: 100%; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    ${config.showLogo && config.logoBase64 ? `<img src="${config.logoBase64}" alt="Logo" class="logo" />` : ''}
    <div class="center">
      ${companyName ? `<div class="muted" style="font-size:${isThermal ? '10px' : '12px'};margin-bottom:4px;">${escapeHtml(companyName)}</div>` : ''}
      <div class="title">${escapeHtml(header)}</div>
      ${subHeader ? `<div class="subtitle muted">${renderMultiline(subHeader)}</div>` : ''}
      ${companyCuit ? `<div class="subtitle muted">CUIT: ${escapeHtml(companyCuit)}</div>` : ''}
    </div>

    <div class="separator"></div>

    <div class="block">
      <div class="block-title">Datos del cierre</div>
      <div class="row"><span class="label">Cierre</span><span class="value">${formatDateTimeAR(cierre?.fecha_cierre)}</span></div>
      <div class="row"><span class="label">Apertura</span><span class="value">${formatDateTimeAR(cierre?.fecha_apertura)}</span></div>
      <div class="row"><span class="label">Operador</span><span class="value">${escapeHtml(cierre?.usuario_email || 'Usuario')}</span></div>
      <div class="row"><span class="label">Referencia</span><span class="value">#${escapeHtml(String(cierre?.id || '').slice(0, 8).toUpperCase())}</span></div>
    </div>

    <div class="block">
      <div class="block-title">Detalle por metodo</div>
      ${config.showPaymentMethod ? `
        <table>
          <thead>
            <tr>
              <th>Metodo</th>
              ${showTheoretical ? '<th class="right">Teorico</th>' : ''}
              <th class="right">Real</th>
              ${showTheoretical ? '<th class="right">Dif.</th>' : ''}
            </tr>
          </thead>
          <tbody>${resumenRows}</tbody>
        </table>
      ` : '<div class="empty">La configuracion actual oculta el detalle por metodo.</div>'}
    </div>

    ${showTheoretical ? `
    <div class="block">
      <div class="block-title">Desglose del turno</div>
      <div class="row"><span class="label">Ingresos (ventas + manuales)</span><span class="value positive">$ ${money(cierre?.total_ingresos)}</span></div>
      <div class="row"><span class="label">Egresos / pagos</span><span class="value negative">$ ${money(cierre?.total_egresos)}</span></div>
      <div class="row"><span class="label">Gastos</span><span class="value negative">$ ${money(cierre?.total_gastos)}</span></div>
    </div>
    ` : ''}

    <div class="totals">
      <div class="total-row"><span>Saldo inicial</span><span>$ ${money(cierre?.saldo_inicial)}</span></div>
      <div class="total-row ${showTheoretical ? '' : 'grand-total'}"><span>Total controlado</span><span>$ ${money(cierre?.total_real)}</span></div>
      ${showTheoretical
        ? `<div class="total-row grand-total ${Number(cierre?.diferencia || 0) < 0 ? 'negative' : 'positive'}"><span>Diferencia</span><span>$ ${money(cierre?.diferencia)}</span></div>`
        : ''}
    </div>

    ${openingNotes ? `
      <div class="block" style="margin-top:12px;">
        <div class="block-title">Observaciones de apertura</div>
        <div style="font-size:${isThermal ? '10px' : '12px'}; line-height:1.5;">${renderMultiline(openingNotes)}</div>
      </div>
    ` : ''}

    <div class="footer">
      ${footer ? `<div>${renderMultiline(footer)}</div>` : ''}
    </div>
  </div>
  <script>
    // La impresión la dispara la ventana padre (handlePrintCierre) de forma
    // sincrónica. Acá solo cerramos la ventana una vez finalizada la impresión.
    window.addEventListener('afterprint', function () {
      window.close();
    });
  </script>
</body>
</html>
    `;
  };

  const handlePrintCierre = (cierre, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    // Guarda de defensa: los botones ya se ocultan sin el permiso, pero el
    // handler se chequea igual para que un render viejo no imprima el arqueo.
    if (!canPrintCloseRef.current) {
      toast.error('No tenes permiso para imprimir el arqueo.');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=960,height=720');
    if (!printWindow) {
      toast.error('Habilita las ventanas emergentes para imprimir el cierre.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildCierreCajaHtml(cierre));
    printWindow.document.close();

    // Disparamos la impresión de forma sincrónica desde la ventana padre.
    // El método anterior (script inline con addEventListener('load')) fallaba:
    // al escribir con document.write sobre una ventana ya abierta, el evento
    // 'load' ya había disparado y el listener nunca corría, dejando el ticket
    // sin imprimir o cerrándose en blanco. Mismo patrón que PedidoDetalle.jsx.
    printWindow.focus();
    printWindow.print();
  };

  const abrirCaja = async (e) => {
    e.preventDefault();
    // ✅ NUEVO offline: no permitimos abrir caja sin red.
    if (!isOnline) {
      toast.error('Sin conexión: no se puede abrir caja hasta restablecer la red.');
      return;
    }
    try {
      // ✅ FIX multi-sucursal (reactivo): persistimos el sitio del turno al abrirlo,
      // tomándolo del SiteContext (mismo que mostró el Topbar al usuario).
      const sitioId = selectedSiteId || null;

      const payload = {
        organization_id: profile.organization_id,
        sitio_id: sitioId,
        fecha_apertura: getSystemOperationTimestamp(),
        estado: 'Abierto',
        saldo_inicial: parseLocalizedNumber(formApertura.saldo_inicial) ?? 0,
        usuario_email: profile?.email || profile?.full_name || 'Usuario',
        observaciones_apertura: formApertura.observaciones || null,
      };

      const { data, error } = await supabase.from('turnos_caja').insert([payload]).select().single();
      if (error) throw error;

      setTurnoActual(data);
      setShowApertura(false);
      setFormApertura({ saldo_inicial: 0, observaciones: '' });
      setMovimientosTurno([]);
      setVentasTurno([]);
      setTab('turno');
      toast.success('Caja abierta correctamente');
    } catch (e2) {
      handleAuthError(e2);
    }
  };

  const registrarMovimiento = async (e) => {
    e.preventDefault();
    if (!turnoActual?.id) return toast.error('No hay turno activo');
    // ✅ NUEVO offline: bloqueamos registro de movimientos sin red para no perder
    // sincronización entre caja, bancos y cuenta corriente del proveedor.
    if (!isOnline) return toast.error('Sin conexión: el movimiento no se puede registrar hasta restablecer la red.');

    try {
      if (!formMov.payment_method_id) return toast.error('Seleccioná un método de pago');
      
      const montoNumerico = parseLocalizedNumber(formMov.monto) ?? 0;
      const isPagoProveedor = formMov.tipo === 'Pago a proveedor' || (formMov.tipo === 'Gasto' && formMov.proveedor_id);

      if (isPagoProveedor && !formMov.proveedor_id) {
          return toast.error('Debes seleccionar un proveedor válido.');
      }

      // ✅ FIX bug #6 multi-sucursal: derivamos el sitio del turno (que sí lo guarda
      // al abrir la caja) y lo propagamos a TODAS las inserciones downstream.
      // Antes gastos/compras/movimientos_financieros/proveedor_movimientos quedaban
      // con sitio_id NULL, así que FlujoFondos los veía como "Consolidado" aunque
      // hubieran ocurrido en una sucursal puntual.
      const sitioIdTurno = turnoActual?.sitio_id || selectedSiteId || null;

      const payloadCaja = {
        turno_id: turnoActual.id,
        organization_id: profile.organization_id,
        tipo: formMov.tipo === 'Pago a proveedor' ? 'Egreso' : formMov.tipo,
        categoria: formMov.categoria || 'Varios',
        monto: montoNumerico,
        descripcion: (formMov.descripcion || '').toUpperCase(),
        payment_method_id: formMov.payment_method_id,
        fecha: getSystemOperationTimestamp(),
      };

      // 1. Insertamos en Caja
      const { data: movCajaData, error: movCajaErr } = await supabase.from('movimientos_caja').insert([payloadCaja]).select().single();
      if (movCajaErr) throw movCajaErr;

      const refTag = `[RefCaja:${movCajaData.id}]`;

      // --- LÓGICA EXTENDIDA (GASTOS Y PAGOS A PROVEEDORES) ---
      if (['Gasto', 'Retiro', 'Pago a proveedor', 'Egreso'].includes(formMov.tipo)) {
          try {
              const conceptoLimpio = (formMov.descripcion || formMov.categoria || 'Movimiento de Caja').toUpperCase();

              // A. BANCOS (Si se pagó por un método ligado a un banco)
              const metodoSeleccionado = metodosCobro.find(m => m.id === formMov.payment_method_id);
              const bankId = metodoSeleccionado?.bank_id || null;
              let nuevoSaldoBanco = null;
              
              if (bankId) {
                  const { data: bankData } = await supabase.from('banks').select('current_balance').eq('id', bankId).single();
                  nuevoSaldoBanco = (Number(bankData?.current_balance) || 0) - montoNumerico;
                  await supabase.from('banks').update({ current_balance: nuevoSaldoBanco }).eq('id', bankId);
              }

              let compraId = null;
              
              // B. LOGICA ESPECIFICA PARA PAGOS A PROVEEDOR
              if (formMov.tipo === 'Pago a proveedor') {
                  const pSel = proveedores.find(p => p.id === formMov.proveedor_id);
                  const provName = pSel ? pSel.razon_social : 'PROVEEDOR DESCONOCIDO';
                  
                  const { data: cData, error: cErr } = await supabase.from('compras').insert([{
                      organization_id: profile.organization_id,
                      proveedor_id: formMov.proveedor_id,
                      proveedor_nombre: provName,
                      fecha: getSystemOperationTimestamp(),
                      tipo_comprobante: 'Pago',
                      numero_comprobante: `CAJA-PAGO-${Math.floor(Math.random()*100000)}`,
                      total_general: montoNumerico,
                      estado: 'Pagada',
                      observaciones: `${conceptoLimpio}. Pago desde Caja ${refTag}`,
                      sitio_id: sitioIdTurno // ✅ FIX bug #6
                  }]).select('id').single();
                  
                  if (cData) {
                      compraId = cData.id;
                      if (pSel) {
                          const nuevoSaldoProv = (Number(pSel.saldo) || 0) - montoNumerico;
                          await supabase.from('proveedores').update({ saldo: nuevoSaldoProv }).eq('id', formMov.proveedor_id);
                          await supabase.from('proveedor_movimientos').insert([
                              {
                                organization_id: profile.organization_id,
                                proveedor_id: formMov.proveedor_id,
                                compra_id: compraId,
                                tipo: 'Pago',
                                monto: -montoNumerico,
                                saldo_resultante: nuevoSaldoProv,
                                fecha: getSystemOperationTimestamp(),
                                sitio_id: sitioIdTurno // ✅ FIX bug #6
                              }
                          ]);
                      }
                  }
              }

              // C. LOGICA ESPECIFICA PARA GASTOS (Gastos operativos)
              if (formMov.tipo === 'Gasto') {
                  const isFijo = formMov.categoria && formMov.categoria.toUpperCase().includes('FIJO');
                  const { error: gastoErr } = await supabase.from('gastos').insert([{
                      organization_id: profile.organization_id,
                      concepto: conceptoLimpio,
                      monto: montoNumerico,
                      fecha: getLocalDateKey(),
                      is_active: true,
                      categoria: isFijo ? 'Fijo' : 'Variable',
                      observaciones: `Gasto generado desde Caja. ${refTag}`,
                      sitio_id: sitioIdTurno // ✅ FIX bug #6
                  }]);
                  if(gastoErr) console.error("Error insertando gasto:", gastoErr);
              }

              // D. FLUJO DE FONDOS (SOLO SI IMPACTÓ UN BANCO REAL)
              if (bankId) {
                  const getCategoria = () => {
                     if (formMov.tipo === 'Pago a proveedor') return 'Pago';
                     if (formMov.tipo === 'Gasto') {
                         const isFijo = formMov.categoria && formMov.categoria.toUpperCase().includes('FIJO');
                         return isFijo ? 'Gasto Fijo' : 'Gasto Variable';
                     }
                     return 'Retiro de Caja';
                  };

                  const { error: mfErr } = await supabase.from('movimientos_financieros').insert([{
                      organization_id: profile.organization_id,
                      bank_id: bankId,
                      sitio_id: sitioIdTurno, // ✅ FIX bug #6
                      tipo: 'Egreso',
                      categoria: getCategoria(),
                      monto: montoNumerico,
                      saldo_resultante: nuevoSaldoBanco,
                      entidad_tipo: formMov.tipo === 'Pago a proveedor' ? 'Proveedor' : null,
                      entidad_id: formMov.tipo === 'Pago a proveedor' ? formMov.proveedor_id : null,
                      documento_tipo: formMov.tipo === 'Pago a proveedor' ? 'Pago' : null,
                      documento_id: compraId,
                      descripcion: `${formMov.tipo === 'Pago a proveedor' ? 'Pago a Prov' : 'Egreso Caja'}: ${conceptoLimpio} ${refTag}`,
                      fecha_operacion: getSystemOperationTimestamp(),
                      user_id: profile.id
                  }]);
                  if(mfErr) console.error("Error insertando flujo de fondos:", mfErr);
              }

          } catch (syncErr) {
              console.error("Error en sincronización extendida:", syncErr);
              toast.error("El movimiento se guardó en caja, pero hubo un problema al sincronizar bancos/gastos.");
          }
      }

      await fetchMovimientosTurno(turnoActual.id);
      await fetchProveedores();
      setShowMovimiento(false);
      setFormMov({ tipo: 'Gasto', categoria: 'Variable', monto: '', descripcion: '', payment_method_id: '', proveedor_id: '' });
      toast.success('Movimiento registrado exitosamente');
    } catch (e2) {
      handleAuthError(e2);
    }
  };

  // NUEVO: Funcionalidad de Anulación y Rollback
  // Corrige el texto de un movimiento manual del turno abierto. No toca monto,
  // tipo, categoria ni medio de pago: eso cambia el arqueo y se resuelve
  // anulando y cargando de nuevo.
  const guardarDescripcionMovimiento = async (mov, texto) => {
    if (!canVoidMovementsRef.current) {
      toast.error('No tenes permiso para modificar movimientos de caja.');
      return false;
    }
    if (!turnoActual?.id || mov.turno_id !== turnoActual.id) {
      toast.error('Solo se pueden editar movimientos del turno abierto.');
      return false;
    }
    if (String(mov.descripcion || '').match(ANULADO_TAG_RE)) {
      toast.error('El movimiento esta anulado.');
      return false;
    }
    const limpio = String(texto || '').replace(ANULADO_TAG_RE, '').replace(/\s+/g, ' ').trim().toUpperCase();
    if (limpio === String(mov.descripcion || '').trim()) return true;

    const { error } = await supabase
      .from('movimientos_caja')
      .update({ descripcion: limpio || null })
      .eq('id', mov.id)
      .eq('organization_id', profile.organization_id)
      .eq('turno_id', turnoActual.id);
    if (error) {
      console.error(error);
      toast.error('No se pudo actualizar la descripción');
      return false;
    }
    setMovimientosTurno((actuales) => actuales.map((m) => (m.id === mov.id ? { ...m, descripcion: limpio || null } : m)));
    toast.success('Descripción actualizada');
    return true;
  };

  const anularMovimientoCaja = async (mov) => {
    // Anular revierte caja, movimientos financieros y cuenta corriente del
    // proveedor: sin el permiso `caja-anular-movimiento` no se ejecuta ni
    // aunque el boton haya quedado pintado por un render previo.
    if (!canVoidMovementsRef.current) {
      toast.error('No tenes permiso para anular movimientos de caja.');
      setConfirmDeleteId(null);
      return;
    }

    setProcessingDeleteId(mov.id);
    const toastId = toast.loading('Deshaciendo movimiento...');
    try {
        const refTag = `[RefCaja:${mov.id}]`;

        // 1. Modificar el registro en Caja (Lo anulamos Lógicamente para que los KPIs lo ignoren)
        // OJO: No podemos modificar "monto: 0" porque la BD tiene un CHECK constraint de monto > 0.
        // Solo modificamos la descripción agregando (ANULADO).
        await supabase.from('movimientos_caja').update({
            descripcion: `(ANULADO) ${mov.descripcion || ''}`
        }).eq('id', mov.id);

        // 2. Buscar y Revertir en Movimientos Financieros (Flujo / Bancos)
        const { data: finMovs } = await supabase.from('movimientos_financieros')
            .select('*').eq('organization_id', profile.organization_id).ilike('descripcion', `%${refTag}%`);
        
        for (const fm of (finMovs || [])) {
            // Revertir Banco
            if (fm.bank_id) {
                const { data: bData } = await supabase.from('banks').select('current_balance').eq('id', fm.bank_id).single();
                if (bData) {
                    const adjustment = fm.tipo === 'Egreso' ? Number(fm.monto) : -Number(fm.monto);
                    const newBalance = Number(bData.current_balance) + adjustment;
                    await supabase.from('banks').update({ current_balance: newBalance }).eq('id', fm.bank_id);
                }
            }
            // Soft-delete del MF (En esta tabla sí se puede poner 0)
            await supabase.from('movimientos_financieros').update({
                is_active: false,
                monto: 0,
                descripcion: `(ANULADO) ${fm.descripcion}`
            }).eq('id', fm.id);
        }

        // 3. Buscar y Revertir en Compras (Historial Proveedores)
        const { data: comprasLinked } = await supabase.from('compras')
            .select('*').eq('organization_id', profile.organization_id).ilike('observaciones', `%${refTag}%`);
        
        for (const cp of (comprasLinked || [])) {
            // Si fue un pago, al anularlo el proveedor vuelve a tener esa deuda
            if (cp.tipo_comprobante === 'Pago' && cp.proveedor_id) {
                const { data: pData } = await supabase.from('proveedores').select('saldo').eq('id', cp.proveedor_id).single();
                if (pData) {
                    const newSaldo = Number(pData.saldo) + Number(cp.total_general);
                    await supabase.from('proveedores').update({ saldo: newSaldo }).eq('id', cp.proveedor_id);
                    await supabase.from('proveedor_movimientos').insert([
                        {
                            organization_id: profile.organization_id,
                            proveedor_id: cp.proveedor_id,
                            compra_id: cp.id,
                            tipo: 'Anulacion de Pago',
                            monto: Number(cp.total_general),
                            saldo_resultante: newSaldo,
                            fecha: getSystemOperationTimestamp(),
                        }
                    ]);
                }
            }
            // Marcar compra como anulada
            await supabase.from('compras').update({
                estado: 'ANULADO',
                total_general: 0,
                observaciones: `(ANULADO) ${cp.observaciones}`
            }).eq('id', cp.id);
        }

        // 4. Buscar y Revertir en Gastos (Estado de resultados)
        const { data: gastosLinked } = await supabase.from('gastos')
            .select('*').eq('organization_id', profile.organization_id).ilike('observaciones', `%${refTag}%`);
        
        for (const gt of (gastosLinked || [])) {
            await supabase.from('gastos').update({
                is_active: false,
                monto: 0,
                observaciones: `(ANULADO) ${gt.observaciones}`
            }).eq('id', gt.id);
        }

        // Refrescar vistas
        await fetchMovimientosTurno(turnoActual.id);
        await fetchProveedores();
        setExpandedId(null);
        setConfirmDeleteId(null);
        toast.success('Movimiento anulado correctamente.', { id: toastId });

    } catch (e) {
        console.error("Error al anular:", e);
        toast.error('Ocurrió un error al intentar revertir los movimientos.', { id: toastId });
    } finally {
        setProcessingDeleteId(null);
    }
  };

  const calcularTeoricoMetodo = (metodo) => {
    if (!turnoActual) return 0;
    let total = 0;

    if ((metodo.name || '').toLowerCase().includes('efectivo')) {
        total += Number(turnoActual.saldo_inicial || 0);
    }

    const ventasDelMetodo = ventasTurno.filter(v => {
        const matchId = v.payment_method_id === metodo.id;
        const matchName = v.payment_method && metodo.name && v.payment_method.toLowerCase() === metodo.name.toLowerCase();
        return matchId || matchName;
    });

    const sumaVentas = ventasDelMetodo.reduce((acc, v) => acc + Number(v.total || 0), 0);
    total += sumaVentas;

    const movsDelMetodo = movimientosTurno.filter(m => {
        const matchId = m.payment_method_id === metodo.id;
        return matchId && m.categoria !== 'Venta' && !String(m.descripcion || '').includes('(ANULADO)');
    });

    movsDelMetodo.forEach((m) => {
      if (m.tipo === 'Ingreso') total += Number(m.monto || 0);
      else total -= Number(m.monto || 0);
    });

    return total;
  };

  const confirmarCierre = async () => {
    if (!turnoActual?.id) return;

    await Promise.all([
         fetchMovimientosTurno(turnoActual.id),
         fetchVentasTurno(turnoActual.fecha_apertura)
    ]);

    const resumen = metodosCobro.map((m) => {
      const teorico = calcularTeoricoMetodo(m);
      const real = parseLocalizedNumber(arqueoReal[m.id]) ?? 0;
      return { nombre: m.name, teorico, real, diferencia: real - teorico };
    });

    const diferenciaTotal = resumen.reduce((acc, r) => acc + Number(r.diferencia || 0), 0);
    const totalRealGlobal = resumen.reduce((acc, r) => acc + Number(r.real || 0), 0);

    // --- Guardas anti-cierre erróneo ---------------------------------------
    // Evitan persistir un cierre claramente mal cargado. Origen: Kilimanjaro
    // 26/06/2026 se cerró con TODO el arqueo en $0 y quedó una diferencia
    // falsa de -1.293.910 sin ninguna alerta.
    const totalTeoricoGlobal = resumen.reduce((acc, r) => acc + Number(r.teorico || 0), 0);
    const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

    // Umbral de diferencia: el mayor entre un piso fijo y un % del teórico,
    // para que aplique tanto en cajas chicas como grandes.
    const UMBRAL_DIF_ABS = 20000;
    const UMBRAL_DIF_PCT = 0.10;
    const umbralDiferencia = Math.max(UMBRAL_DIF_ABS, totalTeoricoGlobal * UMBRAL_DIF_PCT);

    const arqueoVacio = totalRealGlobal === 0 && totalTeoricoGlobal > 0;
    const diferenciaGrande = Math.abs(diferenciaTotal) > umbralDiferencia;

    // La alerta de arqueo en cero es opt-in por empresa (Mi Empresa > Reglas
    // operativas). Por defecto está desactivada: las empresas que cierran en
    // cero a propósito no reciben ninguna alerta. Cuando el arqueo está vacío y
    // la alerta está apagada, tampoco caemos al aviso de diferencia grande —que
    // saltaría igual, porque un arqueo en $0 con teórico > 0 siempre supera el
    // umbral— para no molestar por la puerta de al lado.
    const alertaArqueoCeroActiva = Boolean(organizationData?.alert_arqueo_cero);

    // Estas alertas nombraban el teorico y la diferencia. Con cierre ciego eso
    // le cantaba al cajero justo el numero que no debe ver, y encima en el peor
    // momento: antes de confirmar, todavia a tiempo de "corregir" el arqueo para
    // que cierre clavado. Sin permiso, la alerta avisa pero no revela importes.
    const puedeVerImportesDeControl = canViewFullCloseRef.current;

    if (arqueoVacio) {
      if (alertaArqueoCeroActiva) {
        const ok = window.confirm(
          puedeVerImportesDeControl
            ? `Vas a cerrar con el arqueo en ${fmtMoney(0)} en TODOS los métodos, ` +
              `pero el sistema esperaba ${fmtMoney(totalTeoricoGlobal)}.\n\n` +
              `Esto registraría una diferencia de ${fmtMoney(diferenciaTotal)}.\n\n` +
              `¿Seguro que querés cerrar así? Cancelá y cargá el arqueo real si fue un error.`
            : `Vas a cerrar con el arqueo en ${fmtMoney(0)} en TODOS los métodos.\n\n` +
              `¿Seguro que querés cerrar así? Cancelá y cargá el arqueo real si fue un error.`
        );
        if (!ok) return;
      }
    } else if (diferenciaGrande) {
      const ok = window.confirm(
        puedeVerImportesDeControl
          ? `La diferencia del cierre es ${fmtMoney(diferenciaTotal)} ` +
            `(arqueo real ${fmtMoney(totalRealGlobal)} vs teórico ${fmtMoney(totalTeoricoGlobal)}).\n\n` +
            `Supera el umbral de control de ${fmtMoney(umbralDiferencia)}.\n\n` +
            `¿Confirmás el cierre con esta diferencia?`
          : `El arqueo declarado difiere de lo esperado por el sistema.\n\n` +
            `Revisá el conteo antes de confirmar. ¿Cerrás el turno igual?`
      );
      if (!ok) return;
    }
    // -----------------------------------------------------------------------

    try {
      const { error } = await supabase
        .from('turnos_caja')
        .update({
          fecha_cierre: getSystemOperationTimestamp(),
          estado: 'Cerrado',
          resumen_cierre: resumen,
          diferencia: diferenciaTotal,
          total_real: totalRealGlobal,
          // Desglose del turno persistido para el detalle y el ticket impreso.
          total_ingresos: kpis.ingresos,
          total_egresos: kpis.egresos,
          total_gastos: kpis.gastos,
        })
        .eq('id', turnoActual.id);

      if (error) throw error;

      // Comprobante del cierre que se acaba de hacer. Sin permiso el usuario no
      // ve el historial, y ahi vivia el unico boton de imprimir: lo dejamos a
      // mano en la pestaña Turno para que no pierda su ticket.
      setUltimoCierrePropio({
        ...turnoActual,
        fecha_cierre: getSystemOperationTimestamp(),
        estado: 'Cerrado',
        resumen_cierre: resumen,
        diferencia: diferenciaTotal,
        total_real: totalRealGlobal,
        total_ingresos: kpis.ingresos,
        total_egresos: kpis.egresos,
        total_gastos: kpis.gastos,
      });

      setTurnoActual(null);
      setShowCierre(false);
      setArqueoReal({});
      setMovimientosTurno([]);
      setVentasTurno([]);
      await fetchHistorialCierres();
      setTab(canViewFullCloseRef.current ? 'historial' : 'turno');
      toast.success('Turno cerrado correctamente');
      
    } catch (e) {
      handleAuthError(e);
    }
  };

  const kpis = useMemo(() => {
    if (!turnoActual) return { saldoInicial: 0, ingresos: 0, egresos: 0, gastos: 0, egresosTotal: 0, saldoTeorico: 0 };

    const totalVentas = ventasTurno.reduce((acc, v) => acc + Number(v.total || 0), 0);

    const activos = movimientosTurno.filter((m) => !String(m.descripcion || '').includes('(ANULADO)'));

    const ingresosManuales = activos
      .filter((m) => m.tipo === 'Ingreso' && m.categoria !== 'Venta')
      .reduce((acc, m) => acc + Number(m.monto || 0), 0);

    // Separamos gastos de los demás egresos (Retiro, Pago a proveedor, Egreso)
    // para poder mostrarlos por separado en el resumen y el cierre.
    const gastos = activos
      .filter((m) => m.tipo === 'Gasto')
      .reduce((acc, m) => acc + Number(m.monto || 0), 0);

    const egresos = activos
      .filter((m) => m.tipo !== 'Ingreso' && m.tipo !== 'Gasto')
      .reduce((acc, m) => acc + Number(m.monto || 0), 0);

    const egresosTotal = egresos + gastos;
    const ingresos = totalVentas + ingresosManuales;
    const saldoTeorico = Number(turnoActual.saldo_inicial || 0) + ingresos - egresosTotal;

    return {
        saldoInicial: Number(turnoActual.saldo_inicial || 0),
        ingresos,
        egresos,
        gastos,
        egresosTotal,
        saldoTeorico
    };
  }, [turnoActual, movimientosTurno, ventasTurno]);

  // Lista cruda del turno (ventas + movimientos), sin buscador. Se separa del
  // filtrado porque el permiso acotado (`caja-ver-ultimos-movimientos`) recorta
  // SIEMPRE sobre esta lista: si recortara sobre lo filtrado, el cajero iba
  // buscando termino por termino y reconstruia el turno completo de a X.
  const movimientosCombinados = useMemo(() => {
    let combinados = [];

    const ventasVisuales = ventasTurno.map(v => ({
        id: v.id,
        fecha: v.created_at,
        tipo: 'Ingreso',
        categoria: 'Venta (POS)',
        descripcion: `Venta #${v.id.slice(0,6)}`,
        monto: v.total,
        payment_methods: { name: v.payment_method || 'N/D' },
        isVenta: true
    }));

    const movsVisuales = movimientosTurno.filter(m => m.categoria !== 'Venta');

    combinados = [...ventasVisuales, ...movsVisuales].sort((a,b) => {
        return (b.fecha || '').localeCompare(a.fecha || '');
    });

    return combinados;
  }, [movimientosTurno, ventasTurno]);

  const movimientosFiltrados = useMemo(() => {
    const needle = (q || '').trim().toLowerCase();
    if (!needle) return movimientosCombinados;

    return movimientosCombinados.filter((m) => {
      const a = (m.categoria || '').toLowerCase();
      const b = (m.descripcion || '').toLowerCase();
      const c = (m.payment_methods?.name || '').toLowerCase();
      const d = (m.tipo || '').toLowerCase();
      return a.includes(needle) || b.includes(needle) || c.includes(needle) || d.includes(needle);
    });
  }, [movimientosCombinados, q]);

  const cierresFiltrados = useMemo(() => {
    const needle = (q || '').trim().toLowerCase();
    if (!needle) return historialCierres;

    return historialCierres.filter((c) => {
      const a = (c.usuario_email || '').toLowerCase();
      const b = String(c.saldo_inicial || '').toLowerCase();
      return a.includes(needle) || b.includes(needle);
    });
  }, [historialCierres, q]);

  const compareValues = (a, b, direction = 'asc') => {
    if (typeof a === 'number' || typeof b === 'number') {
      const numA = Number(a || 0);
      const numB = Number(b || 0);
      return direction === 'asc' ? numA - numB : numB - numA;
    }

    const safeA = String(a || '').toLowerCase();
    const safeB = String(b || '').toLowerCase();
    if (safeA < safeB) return direction === 'asc' ? -1 : 1;
    if (safeA > safeB) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedMovimientos = useMemo(() => {
    const items = [...movimientosFiltrados];
    const { key, direction } = movimientoSortConfig;

    return items.sort((a, b) => {
      const valueMap = {
        fecha: [a.fecha, b.fecha],
        tipo: [a.tipo, b.tipo],
        categoria: [a.categoria, b.categoria],
        metodo: [a.payment_methods?.name || '', b.payment_methods?.name || ''],
        descripcion: [a.descripcion, b.descripcion],
        monto: [Number(a.monto || 0), Number(b.monto || 0)],
      };

      const [valueA, valueB] = valueMap[key] || [a.fecha, b.fecha];
      return compareValues(valueA, valueB, direction);
    });
  }, [movimientosFiltrados, movimientoSortConfig]);

  // Tres modos de la pestaña Turno:
  //  - 'completo': lista entera, con buscador y ordenamiento (comportamiento
  //    historico, y el unico posible sin cierre ciego);
  //  - 'ultimos': solo los ultimos N por fecha, sin buscador ni orden, para que
  //    el cajero controle lo que acaba de cargar sin poder barrer el turno;
  //  - 'oculto': ni siquiera eso.
  const modoMovimientos = canViewMovements ? 'completo' : canViewRecentMovements ? 'ultimos' : 'oculto';
  const movimientosRestringidos = modoMovimientos !== 'completo';

  const movimientosVisibles = useMemo(() => {
    if (modoMovimientos === 'completo') return sortedMovimientos;
    if (modoMovimientos === 'oculto') return [];
    // `movimientosCombinados` ya viene ordenado por fecha descendente.
    return movimientosCombinados.slice(0, recentMovementsLimit);
  }, [modoMovimientos, sortedMovimientos, movimientosCombinados, recentMovementsLimit]);

  const sortedCierres = useMemo(() => {
    const items = [...cierresFiltrados];
    const { key, direction } = cierreSortConfig;

    return items.sort((a, b) => {
      const valueMap = {
        fecha_cierre: [a.fecha_cierre, b.fecha_cierre],
        fecha_apertura: [a.fecha_apertura, b.fecha_apertura],
        usuario_email: [a.usuario_email, b.usuario_email],
        saldo_inicial: [Number(a.saldo_inicial || 0), Number(b.saldo_inicial || 0)],
        total_real: [Number(a.total_real || 0), Number(b.total_real || 0)],
        diferencia: [Number(a.diferencia || 0), Number(b.diferencia || 0)],
      };

      const [valueA, valueB] = valueMap[key] || [a.fecha_cierre, b.fecha_cierre];
      return compareValues(valueA, valueB, direction);
    });
  }, [cierresFiltrados, cierreSortConfig]);

  // Mientras el permiso no esté resuelto tratamos al usuario como si NO pudiera
  // ver el historial: un parpadeo de la diferencia ya rompe el cierre ciego.
  const historialDisponible = canViewFullClose && !resolvingCashCloseAccess;
  // Tab efectiva: aunque el estado quedó en 'historial' (por ejemplo, se revocó
  // el permiso en otra pestaña), el render nunca lo muestra sin permiso.
  const activeTab = tab === 'control'
    ? (puedeControlarArqueos ? 'control' : 'turno')
    : (historialDisponible ? tab : 'turno');

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD]" size={40} />
      </div>
    );
  }

  const money = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

  const handleMovimientoSort = (key) => {
    // Sin permiso completo la lista esta recortada a los ultimos N: ordenar por
    // monto dejaria ver los N movimientos mas grandes del turno.
    if (movimientosRestringidos) return;
    setMovimientoSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleCierreSort = (key) => {
    setCierreSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const FilterHint = () => <div className="h-2.5 w-2.5 opacity-30" />;

  const SortableTh = ({ label, sortKey, sortConfig, onSort, align = 'left' }) => {
    const isActive = sortConfig.key === sortKey;

    return (
      <th
        className={`px-3.5 py-3.5 cursor-pointer hover:bg-slate-100 transition-colors select-none ${
          align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
        }`}
        onClick={() => onSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${
          align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'
        }`}>
          <span className="leading-tight text-inherit">{label}</span>
          <span className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <FilterHint />}
          </span>
        </div>
      </th>
    );
  };

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER - Estilo Productos */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Wallet size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Caja</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Control de turnos y efectivo</p>
          </div>
        </div>

        <div data-onboarding-id="caja-actions" className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
          {!turnoActual ? (
            <button
              data-onboarding-id="caja-abrir-btn"
              onClick={() => setShowApertura(true)}
                className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              <Unlock size={16} strokeWidth={2} /> Abrir Caja
            </button>
          ) : (
            <>
              <button
                data-onboarding-id="caja-movimiento-btn"
                onClick={() => setShowMovimiento(true)}
                className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
              >
                <Plus size={16} strokeWidth={2} /> Movimiento / Gasto
              </button>
              
              <button
                data-onboarding-id="caja-cerrar-btn"
                onClick={async () => {
                    if (turnoActual?.id) {
                        const toastId = toast.loading('Calculando cierre...');
                        await Promise.all([
                             fetchMovimientosTurno(turnoActual.id),
                             fetchVentasTurno(turnoActual.fecha_apertura)
                        ]);
                        toast.dismiss(toastId);
                    }
                    setShowCierre(true);
                }}
                className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
              >
                <Lock size={16} strokeWidth={2} /> Cerrar Turno
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPIs - Estilo Productos */}
      {turnoActual && (
        <div data-onboarding-id="caja-kpis" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5 mb-4">
          <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-[#51A7FD]/30 transition-all">
            <div className="flex items-center justify-between relative z-10">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo Inicial</p>
              <CreditCard size={16} className="text-slate-300 group-hover:text-[#51A7FD] transition-colors" />
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight relative z-10">
              {ocultarImportesDeControl ? 'OCULTO' : `$ ${money(kpis.saldoInicial)}`}
            </p>
            <div className="absolute -right-4 -bottom-4 text-slate-50 opacity-10 group-hover:opacity-20 transition-opacity">
                <CreditCard size={80} />
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-emerald-200 transition-all">
            <div className="flex items-center justify-between relative z-10">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ingresos (Ventas+)</p>
              <TrendingUp size={16} className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-emerald-600 tracking-tight relative z-10">
              {isBlindCashCloseEnabled ? 'OCULTO' : `+ $ ${money(kpis.ingresos)}`}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 relative z-10">
              {isBlindCashCloseEnabled ? 'Cierre ciego activo' : 'Ventas + ingresos manuales'}
            </p>
             <div className="absolute -right-4 -bottom-4 text-emerald-50 opacity-10 group-hover:opacity-20 transition-opacity">
                <TrendingUp size={80} />
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-rose-200 transition-all">
            <div className="flex items-center justify-between relative z-10">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Egresos / Pagos</p>
              <TrendingDown size={16} className="text-slate-300 group-hover:text-rose-500 transition-colors" />
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-rose-600 tracking-tight relative z-10">
              {ocultarImportesDeControl ? 'OCULTO' : `- $ ${money(kpis.egresosTotal)}`}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 relative z-10">
              {ocultarImportesDeControl
                ? 'Cierre ciego activo'
                : `Gastos $ ${money(kpis.gastos)} · Otros $ ${money(kpis.egresos)}`}
            </p>
            <div className="absolute -right-4 -bottom-4 text-rose-50 opacity-10 group-hover:opacity-20 transition-opacity">
                <TrendingDown size={80} />
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-[#0B2A36]/20 transition-all">
            <div className="flex items-center justify-between relative z-10">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                {isBlindCashCloseEnabled ? 'Arqueo Ciego' : 'Saldo Teórico'}
              </p>
              <ArrowRightLeft size={16} className="text-slate-300 group-hover:text-[#0B2A36] transition-colors" />
            </div>
            <p className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight relative z-10">
              {isBlindCashCloseEnabled ? 'OCULTO' : `$ ${money(kpis.saldoTeorico)}`}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 relative z-10">
              {isBlindCashCloseEnabled ? 'Sin referencia visible' : 'Referencia interna'}
            </p>
             <div className="absolute -right-4 -bottom-4 text-slate-50 opacity-10 group-hover:opacity-20 transition-opacity">
                <ArrowRightLeft size={80} />
            </div>
          </div>
        </div>
      )}

      {/* FILTROS Y TABS - Estilo Productos */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-3">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
            <input
                value={activeTab === 'turno' && movimientosRestringidos ? '' : q}
                onChange={(e) => setQ(e.target.value)}
                disabled={activeTab === 'turno' && movimientosRestringidos}
                placeholder={
                  activeTab === 'turno' && movimientosRestringidos
                    ? 'BÚSQUEDA NO DISPONIBLE'
                    : 'BUSCAR MOVIMIENTO, DESCRIPCIÓN...'
                }
                className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          
          <div data-onboarding-id="caja-tabs" className="flex items-center gap-2 bg-slate-50 rounded-xl p-1 border border-slate-100 w-full lg:w-auto">
                <button
                onClick={() => setTab('turno')}
                className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                    activeTab === 'turno' ? 'bg-white text-[#0B2A36] shadow-sm border border-slate-100' : 'text-slate-400 hover:text-slate-600'
                }`}
                >
                Turno Actual
                </button>
                {historialDisponible && (
                <button
                onClick={() => setTab('historial')}
                className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                    activeTab === 'historial' ? 'bg-white text-[#0B2A36] shadow-sm border border-slate-100' : 'text-slate-400 hover:text-slate-600'
                }`}
                >
                Historial interno
                </button>
                )}
                {puedeControlarArqueos && (
                <button
                onClick={() => setTab('control')}
                className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all inline-flex items-center gap-2 ${
                    activeTab === 'control' ? 'bg-white text-[#0B2A36] shadow-sm border border-slate-100' : 'text-slate-400 hover:text-slate-600'
                }`}
                >
                Control de arqueos
                {pendientesControl > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#0B2A36] text-white text-[8px] font-black">
                    {pendientesControl > 99 ? '99+' : pendientesControl}
                  </span>
                )}
                </button>
                )}
          </div>
        </div>
      </div>

      {/* COMPROBANTE DEL CIERRE RECIÉN HECHO — solo para quien no ve el historial.
          Sin esto, activar el cierre ciego le sacaba al cajero la posibilidad de
          imprimir su propio arqueo. */}
      {!historialDisponible && ultimoCierrePropio && canPrintClose && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
              <Check size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Turno cerrado</p>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
                Control declarado $ {money(ultimoCierrePropio.total_real)} · {formatDateTimeAR(ultimoCierrePropio.fecha_cierre)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={(event) => handlePrintCierre(ultimoCierrePropio, event)}
            className="inline-flex items-center justify-center gap-2 bg-[#0B2A36] text-white text-[9px] font-black px-4 py-2.5 rounded-xl uppercase tracking-widest hover:brightness-110 transition-all shadow-sm shrink-0"
          >
            <Printer size={12} />
            Imprimir comprobante
          </button>
        </div>
      )}

      {/* LISTADOS - Estilo Productos */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-10">
        <div className="bg-[#0B2A36] px-6 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              {activeTab === 'turno'
                ? 'Detalle de Movimientos y Ventas'
                : activeTab === 'control'
                  ? 'Control de arqueos por sucursal'
                  : 'Historial interno de cierres'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {activeTab !== 'control' && (
            <button
                onClick={async () => {
                    const toastId = toast.loading("Actualizando...");
                    if (activeTab === 'turno' && turnoActual?.id) {
                        await Promise.all([
                             fetchMovimientosTurno(turnoActual.id),
                             fetchVentasTurno(turnoActual.fecha_apertura)
                        ]);
                    } else if(activeTab === 'turno') {
                        await checkTurnoActivo();
                    } else {
                        await fetchHistorialCierres();
                    }
                    toast.success("Actualizado", { id: toastId });
                }}
                className="p-1.5 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all hover:rotate-180 duration-500"
            >
                <RefreshCcw size={14} />
            </button>
            )}
          </div>
        </div>

        {activeTab === 'control' ? (
        <ControlArqueosPanel
          organization={organization}
          profile={profile}
          onPendientesChange={setPendientesControl}
          onVerDetalle={(turnoId) => goToArqueoDetalle(turnoId, 'control')}
        />
        ) : activeTab === 'turno' ? (
        <>
        {/* Aviso de lista recortada: sin el permiso completo el cajero tiene que
            entender por que no ve el detalle, o va a reportarlo como una falla. */}
        {turnoActual && movimientosRestringidos && (
          <div className="m-3 rounded-2xl border border-amber-100 bg-amber-50/60 px-4 py-3 flex items-start gap-3">
            <Lock size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest">
                {modoMovimientos === 'ultimos'
                  ? `Solo se muestran los ultimos ${recentMovementsLimit} movimientos`
                  : 'Detalle del turno no disponible'}
              </p>
              <p className="text-[10px] font-bold text-amber-600/80 uppercase tracking-widest mt-0.5">
                Cierre ciego activo: el detalle completo requiere permiso
              </p>
            </div>
          </div>
        )}
        {turnoActual && (
        <div data-onboarding-id="caja-list" className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
            {movimientosVisibles.map((m, i) => {
                const isAnulado = String(m.descripcion || '').includes('(ANULADO)');
                const isExpanded = expandedId === m.id;
                return (
                    <div key={`${m.id}-${i}-mobile`} className={`rounded-2xl border shadow-sm overflow-hidden ${isAnulado ? 'bg-rose-50/40 border-rose-100' : 'bg-white border-slate-200'}`}>
                        <button
                            onClick={() => {
                                if (!m.isVenta && !isAnulado && canVoidMovements) {
                                    setExpandedId(isExpanded ? null : m.id);
                                    setConfirmDeleteId(null);
                                }
                            }}
                            className={`w-full p-3 text-left ${!m.isVenta && !isAnulado && canVoidMovements ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className={`text-[10px] font-black uppercase tracking-widest ${isAnulado ? 'text-slate-300 line-through' : 'text-slate-400'}`}>{formatDateAR(m.fecha)}</p>
                                    <p className={`mt-1 text-[11px] font-black uppercase truncate ${isAnulado ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{m.categoria}</p>
                                    <p className={`mt-1 text-[10px] font-bold uppercase truncate ${isAnulado ? 'text-slate-300' : 'text-slate-500'}`}>{m.descripcion || '-'}</p>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-xl border ${m.tipo === 'Ingreso' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>{m.tipo}</span>
                                        <span className="text-[9px] font-black uppercase px-2 py-1 rounded-xl border bg-slate-100 text-slate-600 border-slate-200">{m.payment_methods?.name || '-'}</span>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className={`text-[12px] font-black ${isAnulado ? 'text-slate-400 line-through' : (m.tipo === 'Ingreso' ? 'text-emerald-600' : 'text-rose-600')}`}>{m.tipo === 'Ingreso' ? '+' : '-'}$ {money(m.monto)}</p>
                                    {!m.isVenta && !isAnulado && (
                                        <div className="mt-2 flex justify-end">
                                            {isExpanded ? <ChevronUp size={16} className="text-[#51A7FD]" /> : <ChevronDown size={16} className="text-slate-300" />}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </button>
                        {isExpanded && !m.isVenta && !isAnulado && canVoidMovements && (
                            <div className="px-3 pb-3">
                                <div className="border-t border-slate-100 pt-3">
                                    <div className="bg-white border border-rose-100 p-3 rounded-xl shadow-inner space-y-3">
                                        <div className="flex items-start gap-3">
                                            <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5"/>
                                            <div>
                                                <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Opciones de Registro</p>
                                                <p className="text-[10px] font-bold text-slate-500 mt-0.5">Si anulas este registro, se revertiran los saldos vinculados.</p>
                                            </div>
                                        </div>
                                        <EditarDescripcionMovimiento mov={m} onSave={guardarDescripcionMovimiento} />
                                        {confirmDeleteId === m.id ? (
                                            <div className="flex flex-col sm:flex-row gap-2 animate-in zoom-in bg-rose-50 p-2 rounded-xl border border-rose-200">
                                                <span className="text-[10px] font-black text-rose-600 uppercase px-1 sm:px-2 self-center">Confirmar?</span>
                                                <button onClick={() => anularMovimientoCaja(m)} disabled={processingDeleteId === m.id} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-rose-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-rose-600 transition-colors">
                                                    {processingDeleteId === m.id ? <RefreshCcw size={12} className="animate-spin" /> : <Check size={12} />} Si, borrar
                                                </button>
                                                <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-3 py-2 bg-white border border-slate-200 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-50 transition-colors">
                                                    No
                                                </button>
                                            </div>
                                        ) : (
                                            <button onClick={() => setConfirmDeleteId(m.id)} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">
                                                <Trash2 size={14} /> Anular Registro
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
            {movimientosVisibles.length === 0 && (
                <div className="py-16 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">Sin movimientos aun en este turno</div>
            )}
        </div>
        )}
        <div data-onboarding-id="caja-list" className="hidden lg:block overflow-x-auto">
            {!turnoActual ? (
                 <div className="py-20 text-center opacity-40 italic text-[10px] font-black uppercase tracking-widest flex flex-col items-center gap-2">
                    <AlertCircle size={32} />
                    Caja cerrada. Abre un turno para ver movimientos.
                 </div>
            ) : (
                <table className="w-full text-left min-w-[760px]">
                <thead>
                    <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <SortableTh label="Fecha" sortKey="fecha" sortConfig={movimientoSortConfig} onSort={handleMovimientoSort} />
                    <SortableTh label="Tipo" sortKey="tipo" sortConfig={movimientoSortConfig} onSort={handleMovimientoSort} />
                    <SortableTh label="Categoria" sortKey="categoria" sortConfig={movimientoSortConfig} onSort={handleMovimientoSort} />
                    <SortableTh label="Metodo" sortKey="metodo" sortConfig={movimientoSortConfig} onSort={handleMovimientoSort} />
                    <SortableTh label="Descripcion" sortKey="descripcion" sortConfig={movimientoSortConfig} onSort={handleMovimientoSort} />
                    <SortableTh label="Importe" sortKey="monto" sortConfig={movimientoSortConfig} onSort={handleMovimientoSort} align="right" />
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {movimientosVisibles.map((m, i) => {
                        const isAnulado = String(m.descripcion || '').includes('(ANULADO)');
                        return (
                            <React.Fragment key={m.id + i}>
                                <tr 
                                    onClick={() => {
                                        // Expandimos si no es una venta y si no está ya anulado
                                        if(!m.isVenta && !isAnulado && canVoidMovements) {
                                            setExpandedId(expandedId === m.id ? null : m.id);
                                            setConfirmDeleteId(null);
                                        }
                                    }}
                                    className={`transition-all group ${!m.isVenta && !isAnulado && canVoidMovements ? 'hover:bg-blue-50/20 cursor-pointer' : ''} ${isAnulado ? 'bg-rose-50/30 opacity-70' : ''}`}
                                >
                                    <td className={`px-6 py-4 text-[11px] font-bold ${isAnulado ? 'text-slate-400 line-through' : 'text-slate-500'}`}>
                                    {formatDateAR(m.fecha)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-md border ${m.tipo === 'Ingreso' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>
                                        {m.tipo}
                                    </span>
                                    </td>
                                    <td className={`px-6 py-4 text-[10px] font-black uppercase ${isAnulado ? 'text-slate-400' : 'text-slate-700'}`}>{m.categoria}</td>
                                    <td className={`px-6 py-4 text-[10px] font-bold uppercase transition-colors ${isAnulado ? 'text-slate-400' : 'text-slate-500 group-hover:text-[#51A7FD]'}`}>{m.payment_methods?.name || '-'}</td>
                                    <td className={`px-6 py-4 text-[10px] font-bold uppercase truncate max-w-[200px] ${isAnulado ? 'text-slate-400' : 'text-slate-700'}`}>{m.descripcion || '-'}</td>
                                    <td className={`px-6 py-4 text-right text-[12px] font-black ${isAnulado ? 'text-slate-400 line-through' : (m.tipo === 'Ingreso' ? 'text-emerald-600' : 'text-rose-600')}`}>
                                    {m.tipo === 'Ingreso' ? '+' : '-'}$ {money(m.monto)}
                                    </td>
                                </tr>
                                
                                {/* Detalle Expandible para Anulación */}
                                {expandedId === m.id && !m.isVenta && !isAnulado && canVoidMovements && (
                                    <tr className="bg-slate-50 animate-in fade-in slide-in-from-top-1">
                                        <td colSpan="6" className="px-6 py-4 border-b border-slate-100">
                                            <div className="flex items-center justify-between bg-white border border-rose-100 p-4 rounded-xl shadow-inner">
                                                <div className="flex items-center gap-3">
                                                    <AlertCircle size={18} className="text-rose-500"/>
                                                    <div>
                                                        <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Opciones de Registro</p>
                                                        <p className="text-[10px] font-bold text-slate-500 mt-0.5">Si anulas este registro, se revertirán los saldos en Caja, Bancos y Proveedores vinculados.</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex flex-1 items-center justify-end gap-2 pl-4">
                                                <EditarDescripcionMovimiento mov={m} onSave={guardarDescripcionMovimiento} />
                                                {confirmDeleteId === m.id ? (
                                                    <div className="flex items-center gap-2 animate-in zoom-in bg-rose-50 p-1.5 rounded-xl border border-rose-200">
                                                        <span className="text-[10px] font-black text-rose-600 uppercase px-2">¿Confirmar?</span>
                                                        <button 
                                                            onClick={() => anularMovimientoCaja(m)}
                                                            disabled={processingDeleteId === m.id}
                                                            className="flex items-center gap-1 px-3 py-1.5 bg-rose-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-rose-600 transition-colors"
                                                        >
                                                            {processingDeleteId === m.id ? <RefreshCcw size={12} className="animate-spin" /> : <Check size={12} />} Sí, borrar
                                                        </button>
                                                        <button 
                                                            onClick={() => setConfirmDeleteId(null)}
                                                            className="px-3 py-1.5 bg-white border border-slate-200 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-50 transition-colors"
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button 
                                                        onClick={() => setConfirmDeleteId(m.id)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm"
                                                    >
                                                        <Trash2 size={14} /> Anular Registro
                                                    </button>
                                                )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                    {movimientosVisibles.length === 0 && (
                        <tr><td colSpan="6" className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">Sin movimientos aun en este turno</td></tr>
                    )}
                </tbody>
                </table>
            )}
        </div>
        </>
        ) : (
            <>
            <div className="lg:hidden p-4 space-y-2">
                {sortedCierres.map((c) => (
                <div
                    key={c.id}
                    onClick={() => goToArqueoDetalle(c.id)}
                    className="grid grid-cols-2 md:grid-cols-7 items-center px-6 py-4 bg-white border border-slate-100 rounded-2xl hover:shadow-md hover:border-[#51A7FD]/30 transition-all cursor-pointer group"
                >
                    <div className="text-[10px] font-black text-slate-600 flex items-center gap-2 col-span-2 md:col-span-1">
                    <Calendar size={14} className="text-slate-300" />
                    {formatFechaCierre(c.fecha_cierre)}
                    </div>
                    <div className="text-[10px] font-bold text-slate-400 hidden md:block">
                    {formatHoraTurno(c.fecha_apertura)}
                    </div>
                    <div className="text-[10px] font-black text-slate-500 truncate pr-4 flex items-center gap-2 col-span-2 md:col-span-1">
                    <User size={14} className="text-slate-300" />
                    {c.usuario_email?.split('@')[0].toUpperCase()}
                    </div>
                    <div className="text-right text-[10px] font-bold text-slate-400 uppercase">Inicial: $ {money(c.saldo_inicial)}</div>
                    <div className="text-right text-[11px] font-black text-slate-800">Control: $ {money(c.total_real)}</div>
                    <div className={`text-right text-[10px] font-black ${Number(c.diferencia || 0) < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                    Dif: $ {money(c.diferencia)}
                    </div>
                    <div className="flex justify-end">
                      {canPrintClose && (
                      <button
                        type="button"
                        onClick={(event) => handlePrintCierre(c, event)}
                        className="inline-flex items-center gap-2 bg-[#0B2A36] text-white text-[9px] font-black px-3 py-2 rounded-xl uppercase tracking-widest hover:brightness-110 transition-all shadow-sm"
                      >
                        <Printer size={12} />
                        Imprimir
                      </button>
                      )}
                    </div>
                </div>
                ))}
                {sortedCierres.length === 0 && (
                 <div className="p-16 text-center text-slate-300 font-bold italic text-sm bg-white rounded-2xl border border-dashed border-slate-200">
	                    No hay cierres registrados.
                 </div>
                )}
            </div>
            <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[820px]">
                    <thead>
                        <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                            <SortableTh label="Fecha Cierre" sortKey="fecha_cierre" sortConfig={cierreSortConfig} onSort={handleCierreSort} />
                            <SortableTh label="Hora Apertura" sortKey="fecha_apertura" sortConfig={cierreSortConfig} onSort={handleCierreSort} />
                            <SortableTh label="Operador" sortKey="usuario_email" sortConfig={cierreSortConfig} onSort={handleCierreSort} />
                            <SortableTh label="Inicial" sortKey="saldo_inicial" sortConfig={cierreSortConfig} onSort={handleCierreSort} align="right" />
                            <SortableTh label="Control" sortKey="total_real" sortConfig={cierreSortConfig} onSort={handleCierreSort} align="right" />
                            <SortableTh label="Diferencia" sortKey="diferencia" sortConfig={cierreSortConfig} onSort={handleCierreSort} align="right" />
                            <th className="px-3.5 py-3.5 text-right">Impresion</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {sortedCierres.map((c) => (
                            <tr
                                key={c.id}
                                onClick={() => goToArqueoDetalle(c.id)}
                                className="cursor-pointer hover:bg-blue-50/20 transition-all"
                            >
                                <td className="px-3.5 py-3.5 text-[10px] font-black text-slate-600">
                                    {formatFechaCierre(c.fecha_cierre)}
                                </td>
                                <td className="px-3.5 py-3.5 text-[10px] font-bold text-slate-400">
                                    {formatHoraTurno(c.fecha_apertura)}
                                </td>
                                <td className="px-3.5 py-3.5 text-[10px] font-black text-slate-500 uppercase">
                                    {c.usuario_email?.split('@')[0].toUpperCase()}
                                </td>
                                <td className="px-3.5 py-3.5 text-right text-[10px] font-bold text-slate-500">$ {money(c.saldo_inicial)}</td>
                                <td className="px-3.5 py-3.5 text-right text-[11px] font-black text-slate-800">$ {money(c.total_real)}</td>
                                <td className={`px-3.5 py-3.5 text-right text-[10px] font-black ${Number(c.diferencia || 0) < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                                    $ {money(c.diferencia)}
                                </td>
                                <td className="px-3.5 py-3.5 text-right">
                                    {canPrintClose && (
                                    <button
                                      type="button"
                                      onClick={(event) => handlePrintCierre(c, event)}
                                      className="inline-flex items-center gap-2 bg-[#0B2A36] text-white text-[9px] font-black px-3 py-2 rounded-xl uppercase tracking-widest hover:brightness-110 transition-all shadow-sm"
                                    >
                                      <Printer size={12} />
                                      Imprimir
                                    </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {sortedCierres.length === 0 && (
                            <tr>
                                <td colSpan="7" className="p-16 text-center text-slate-300 font-bold italic text-sm">No hay cierres registrados.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            </>
        )}
      </div>

      {/* MODAL: APERTURA */}
      {showApertura && (
        <div className="fixed inset-0 bg-[#0B2A36]/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl animate-nova-in mx-2 md:mx-0 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-8">
              <div className="bg-emerald-50 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-emerald-600 shadow-inner">
                <Unlock size={32} />
              </div>
              <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Apertura de Caja</h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Declarar saldo inicial para comenzar el turno.</p>
            </div>

            <form onSubmit={abrirCaja} className="space-y-6">
              <div>
                <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">Saldo Inicial</label>
                <LocalizedNumberInput
                  decimals={2}
                  selectOnFocus
                  className="w-full bg-slate-50 border-2 border-slate-50 p-4 rounded-2xl text-2xl font-black text-center outline-none focus:border-[#51A7FD] transition-all mt-1 shadow-inner"
                  value={formApertura.saldo_inicial}
                  onValueChange={(display) => setFormApertura({ ...formApertura, saldo_inicial: display })}
                  placeholder="0,00"
                />
              </div>

              <div>
                <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">Observaciones</label>
                <input
                  type="text"
                  className="w-full bg-slate-50 border-2 border-slate-50 p-3 rounded-2xl text-[11px] font-bold outline-none focus:border-[#51A7FD] transition-all mt-1 uppercase"
                  value={formApertura.observaciones}
                  onChange={(e) => setFormApertura({ ...formApertura, observaciones: e.target.value })}
                  placeholder="Opcional"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#3ff2aa] text-[#0B2A36] py-4 rounded-2xl font-black uppercase tracking-[0.2em] shadow-xl hover:brightness-105 transition-all"
              >
                Confirmar Apertura
              </button>

              <button
                type="button"
                onClick={() => setShowApertura(false)}
                className="w-full text-slate-400 font-bold uppercase tracking-widest text-[10px] flex items-center justify-center gap-2"
              >
                <X size={14} /> Cancelar
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: MOVIMIENTO / GASTO */}
      {showMovimiento && (
        <div className="fixed inset-0 bg-[#0B2A36]/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] p-6 md:p-8 shadow-2xl animate-nova-in mx-2 md:mx-0 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-black text-slate-800 uppercase">Registrar Movimiento</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
                  Impacta en el turno activo y contabilidad
                </p>
              </div>
              <button
                onClick={() => setShowMovimiento(false)}
                className="p-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={registrarMovimiento} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo</label>
                  <select
                    value={formMov.tipo}
                    onChange={(e) => {
                        const newType = e.target.value;
                        setFormMov({ 
                            ...formMov, 
                            tipo: newType,
                            categoria: newType === 'Gasto' || newType === 'Pago a proveedor' ? 'Variable' : (newType === 'Ingreso' ? 'Varios' : formMov.categoria),
                            proveedor_id: newType !== 'Gasto' && newType !== 'Pago a proveedor' ? '' : formMov.proveedor_id
                        });
                    }}
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                  >
                    <option value="Ingreso">Ingreso</option>
                    {/* <option value="Egreso">Egreso (Gral)</option> */}
                    <option value="Gasto">Gasto / Compra</option>
                    <option value="Pago a proveedor">Pago a Proveedor (Cta Cte)</option>
                    <option value="Retiro">Retiro de Caja</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Método de Pago</label>
                  <select
                    value={formMov.payment_method_id}
                    onChange={(e) => setFormMov({ ...formMov, payment_method_id: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
                  >
                    <option value="">Seleccionar...</option>
                    {metodosCobro.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(formMov.tipo === 'Gasto' || formMov.tipo === 'Pago a proveedor') && (
                  <div className="space-y-1 animate-in fade-in zoom-in-95">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Proveedor Asociado {formMov.tipo === 'Pago a proveedor' ? '(Obligatorio)' : '(Opcional)'}</label>
                      <select
                          value={formMov.proveedor_id}
                          onChange={(e) => setFormMov({ ...formMov, proveedor_id: e.target.value })}
                          className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner"
                          required={formMov.tipo === 'Pago a proveedor'}
                      >
                          <option value="">-- SELECCIONAR PROVEEDOR --</option>
                          {proveedores.map(p => (
                              <option key={p.id} value={p.id}>{p.razon_social} (Saldo: ${money(p.saldo)})</option>
                          ))}
                      </select>
                  </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Categoría</label>
                  <input
                    value={formMov.categoria}
                    onChange={(e) => setFormMov({ ...formMov, categoria: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase"
                    placeholder="Ej: Servicios, Insumos..."
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Monto</label>
                  <LocalizedNumberInput
                    decimals={2}
                    selectOnFocus
                    value={formMov.monto}
                    onValueChange={(display) => setFormMov({ ...formMov, monto: display })}
                    className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-inner text-rose-600"
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Descripción de Operación</label>
                <input
                  value={formMov.descripcion}
                  onChange={(e) => setFormMov({ ...formMov, descripcion: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase"
                  placeholder="Detalle del movimiento para la auditoría..."
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#3ff2aa] text-[#0B2A36] py-4 rounded-2xl font-black uppercase tracking-[0.2em] shadow-xl hover:brightness-105 transition-all flex items-center justify-center gap-3 mt-4"
              >
                <Plus size={18} strokeWidth={3} /> Guardar Registro
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: CIERRE / ARQUEO */}
      {showCierre && turnoActual && (
        <div className="fixed inset-0 bg-[#0B2A36]/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white w-full max-w-2xl rounded-[2.5rem] p-6 shadow-2xl animate-nova-in mx-2 md:mx-0 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-black text-slate-800 uppercase">Arqueo y cierre de turno</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
                  {isBlindCashCloseEnabled ? 'Cierre ciego activo: declarar real sin referencia teórica' : 'Declarar real por método y confirmar'}
                </p>
              </div>
              <button
                onClick={() => setShowCierre(false)}
                className="p-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-auto rounded-2xl border border-slate-100 custom-scrollbar">
              <table className={`w-full ${isBlindCashCloseEnabled ? 'min-w-[320px]' : 'min-w-[500px]'}`}>
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-[9px] font-black uppercase tracking-[0.2em]">
                    <th className="p-3 text-left">Método</th>
                    {!isBlindCashCloseEnabled && <th className="p-3 text-center">Teórico</th>}
                    <th className="p-3 text-center">Real</th>
                    {!isBlindCashCloseEnabled && <th className="p-3 text-right">Diferencia</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {metodosCobro.map((metodo) => {
                    const teorico = calcularTeoricoMetodo(metodo);
                    const real = parseLocalizedNumber(arqueoReal[metodo.id]) ?? 0;
                    const dif = real - teorico;

                    return (
                      <tr key={metodo.id}>
                        <td className="p-3 font-bold text-slate-700 uppercase tracking-tight">{metodo.name}</td>
                        {!isBlindCashCloseEnabled && (
                          <td className="p-3 text-center font-medium">$ {money(teorico)}</td>
                        )}
                        <td className="p-3">
                          <LocalizedNumberInput
                            decimals={2}
                            selectOnFocus
                            className="w-full py-2 px-3 border-2 border-slate-50 rounded-2xl text-center font-bold outline-none focus:border-[#51A7FD] transition-all"
                            placeholder="0,00"
                            value={arqueoReal[metodo.id] ?? ''}
                            onValueChange={(display) => setArqueoReal({ ...arqueoReal, [metodo.id]: display })}
                          />
                        </td>
                        {!isBlindCashCloseEnabled && (
                          <td className={`p-3 text-right font-black ${dif < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                            $ {money(dif)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              onClick={confirmarCierre}
              className="w-full bg-rose-500 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] shadow-xl hover:bg-rose-600 transition-all flex items-center justify-center gap-4 mt-6"
            >
              <Lock size={20} /> Finalizar Turno y Cerrar Caja
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default Caja;


